import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { UrlApi } from "../../../services/apirest";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import moment from "moment";
import "moment/locale/es";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

moment.locale("es");

const AgregarSolicitud = ({
  open,
  onClose,
  row,
  showToast,
  onSuccess,
  periodo,
  isEditMode,
}) => {
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(false);

  const periodoKey = periodo ? `_${periodo}` : "";
  const fechaInicioEdicion = row?.[`inicio${periodoKey}`]
    ? moment(row[`inicio${periodoKey}`])
    : null;
  const diasSolicitadosEdicion = row?.[`dias${periodoKey}`] ?? null;

  const [body, setBody] = useState({
    diasDeVacaciones: isEditMode ? diasSolicitadosEdicion : "",
    fechaInicio: isEditMode
      ? fechaInicioEdicion
      : moment(`01/10/${row.anio}`, "DD/MM/YYYY"),
  });

  const getFeriados = async () => {
    try {
      const response = await fetch(`${UrlApi}/feriados`);
      const data = await response.json();
      setFeriados(
        data.map((f) => ({
          ...f,
          key: moment(f.fecha).format("MM-DD"),
        }))
      );
    } catch (err) {
      console.error("Error cargando feriados:", err);
    }
  };

  useEffect(() => {
    getFeriados();
  }, []);

  const diasDeVacaciones = isEditMode
    ? (row.remainingDays ?? 0) + (diasSolicitadosEdicion ?? 0)
    : row.remainingDays ?? row.totalDays;

  // console.log("Periodo: ", periodo);
  // console.log("Is edit mode: ", isEditMode);
  // console.log("ROW: ", row);

  const p1 = row.period_id_1;
  const p2 = row.period_id_2;
  const p3 = row.period_id_3;

  const periods = [p1, p2, p3];

  const periodosFiltrados = periods.filter(Boolean);

  const opciones =
    periodosFiltrados.length === 2 && !isEditMode
      ? [diasDeVacaciones]
      : diasDeVacaciones <= 7
      ? [diasDeVacaciones]
      : diasDeVacaciones < 14
      ? [7, diasDeVacaciones]
      : diasDeVacaciones < 21
      ? [7, 14]
      : diasDeVacaciones < 28
      ? [7, 14, 21]
      : diasDeVacaciones < 35
      ? [7, 14, 21, 28]
      : [7, 14, 21, 28, 35];

  const shouldDisableDate = (date) => {
    if (!date || !row?.anio) return true;

    const d = moment(date).startOf("day");
    const year = Number(row.anio);
    const minDate = moment(`${year}-10-01`).startOf("day");
    const maxDate = moment(`${year + 1}-04-30`).endOf("day");
    if (!d.isBetween(minDate, maxDate, null, "[]")) return true;

    const isHoliday = feriados.some((f) => f.key === d.format("MM-DD"));

    const wasHoliday = (daysAgo) =>
      feriados.some(
        (f) => f.key === d.clone().subtract(daysAgo, "day").format("MM-DD")
      );

    const isMonday = d.day() === 1 && !isHoliday;

    const isTuesdayAfterHoliday = d.day() === 2 && !isHoliday && wasHoliday(1);

    const isWednesdayAfterTwoHolidays =
      d.day() === 3 && !isHoliday && wasHoliday(1) && wasHoliday(2);

    const isAllowed =
      isMonday || isTuesdayAfterHoliday || isWednesdayAfterTwoHolidays;

    return !isAllowed;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const username = localStorage.getItem("user");

    if (!body.diasDeVacaciones || !body.fechaInicio) {
      showToast("Tenés que completar los datos antes de enviar", "warning");
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const periodId = row[`period_id_${periodo}`];

        const fechaCorregida = moment(body.fechaInicio)
          .add(1, "day")
          .format("YYYY-MM-DD");

        const payload = {
          dias: body.diasDeVacaciones,
          fecha_inicio: fechaCorregida,
        };

        await axios.put(`${UrlApi}/vacations/${periodId}`, payload);
        showToast(`Período ${periodo} actualizado correctamente.`, "success");
      } else {
        const payload = {
          year: row.anio,
          days: body.diasDeVacaciones,
          startDate: body.fechaInicio.format("YYYY-MM-DD"),
        };

        await axios.post(
          `${UrlApi}/vacations/${row.empleadoId}/periods`,
          payload
        );
        showToast(`Período ${periodo} creado correctamente.`, "success");
      }

      onSuccess?.();
      onClose();
      setBody({ diasDeVacaciones: "", fechaInicio: null });
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Ocurrió un error al registrar la solicitud";
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!row) {
    console.warn("⛔ AgregarSolicitud se montó sin 'row'");
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle variant="h4">
        {isEditMode
          ? "Editar solicitud de vacaciones"
          : "Crear nueva solicitud"}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isEditMode
            ? `Editando el período ${periodo} del empleado ${row?.nombre}`
            : `Creando una nueva solicitud para el empleado ${row?.nombre}`}
        </DialogContentText>

        <form id="vac-form" onSubmit={handleSubmit}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {isEditMode
              ? "Modificar período existente"
              : "Registrar nuevo período"}
          </Typography>

          <FormControl fullWidth margin="dense" variant="standard">
            <InputLabel>Días de vacaciones *</InputLabel>
            <Select
              value={body.diasDeVacaciones}
              onChange={(e) =>
                setBody((p) => ({ ...p, diasDeVacaciones: e.target.value }))
              }
            >
              {opciones.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
            <DatePicker
              label="Fecha de inicio (octubre a abril, solo lunes o martes post-feriado)"
              value={body.fechaInicio}
              onChange={(newVal) =>
                setBody((p) => ({ ...p, fechaInicio: newVal }))
              }
              shouldDisableDate={shouldDisableDate}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "dense",
                  variant: "standard",
                },
              }}
            />
          </LocalizationProvider>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="error" disabled={loading}>
          Volver
        </Button>
        <Button
          type="submit"
          form="vac-form"
          color="success"
          variant="contained"
          disabled={loading}
        >
          {loading ? "Guardando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgregarSolicitud;
